import React, { useState, useMemo } from 'react';
import { cn } from '../utils/cn';
import Spinner from '../atoms/Spinner';
import Button from '../atoms/Button';
import { Download, Search, SortAsc, SortDesc, FileText, FileSpreadsheet, X, Filter } from 'lucide-react';
import * as XLSX from 'xlsx';

/**
 * DataTable Component - Enhanced data table with filtering, sorting, and export
 * 
 * @param {Object} props
 * @param {Array} props.columns - Column definitions [{key, label, render?, sortable?, exportRender?, filterType?}]
 * @param {Array} props.data - Table data
 * @param {boolean} props.loading - Loading state
 * @param {boolean} props.striped - Striped rows
 * @param {boolean} props.hover - Hover effect
 * @param {string} props.emptyMessage - Message to show when no data
 * @param {boolean} props.enableSearch - Enable search functionality
 * @param {boolean} props.enableExport - Enable export functionality
 * @param {boolean} props.enableColumnFilters - Enable column-specific filters
 * @param {string} props.exportFileName - Default filename for exports
 * @param {Array} props.searchableFields - Fields to search in (default: all text fields)
 */
const DataTable = ({
  columns = [],
  data = [],
  loading = false,
  striped = false,
  hover = true,
  emptyMessage = 'No data available',
  className = '',
  enableSearch = true,
  enableExport = true,
  enableColumnFilters = false,
  exportFileName = 'export',
  searchableFields = null,
  ...props
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [columnFilters, setColumnFilters] = useState({});

  // Handle sorting
  const handleSort = (columnKey) => {
    const column = columns.find(col => col.key === columnKey);
    if (!column || column.sortable === false) return;

    let direction = 'asc';
    if (sortConfig.key === columnKey && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key: columnKey, direction });
  };

  // Extract plain text from rendered content
  const getPlainTextValue = (col, row) => {
    // If exportRender is defined, use it
    if (col.exportRender) {
      return col.exportRender(row);
    }
    
    // If render function exists
    if (col.render) {
      const rendered = col.render(row);
      
      // Handle React elements/objects
      if (typeof rendered === 'object' && rendered !== null) {
        // Try to extract text from props
        if (rendered.props) {
          if (typeof rendered.props.children === 'string' || typeof rendered.props.children === 'number') {
            return rendered.props.children;
          }
          // Handle nested children
          if (Array.isArray(rendered.props.children)) {
            return rendered.props.children
              .map(child => {
                if (typeof child === 'string' || typeof child === 'number') return child;
                if (child && child.props && (typeof child.props.children === 'string' || typeof child.props.children === 'number')) {
                  return child.props.children;
                }
                return '';
              })
              .join(' ')
              .trim();
          }
        }
        // If it's still an object, try to stringify and clean
        return row[col.key] || '';
      }
      
      // If render returns primitive value
      return rendered;
    }
    
    // Default: return the raw value
    return row[col.key];
  };

  // Filter and sort data
  const processedData = useMemo(() => {
    let filtered = [...data];

    // Apply search filter
    if (searchTerm && enableSearch) {
      filtered = filtered.filter(row => {
        const fieldsToSearch = searchableFields || columns.filter(col => col.key !== 'actions').map(col => col.key);
        return fieldsToSearch.some(field => {
          const value = row[field];
          if (value === null || value === undefined) return false;
          return String(value).toLowerCase().includes(searchTerm.toLowerCase());
        });
      });
    }

    // Apply column filters
    if (enableColumnFilters && Object.keys(columnFilters).length > 0) {
      filtered = filtered.filter(row => {
        return Object.entries(columnFilters).every(([key, filterValue]) => {
          if (!filterValue) return true;
          const value = row[key];
          if (value === null || value === undefined) return false;
          return String(value).toLowerCase().includes(filterValue.toLowerCase());
        });
      });
    }

    // Apply sorting
    if (sortConfig.key) {
      filtered.sort((a, b) => {
        const column = columns.find(col => col.key === sortConfig.key);
        let aVal, bVal;

        // Get sort values - use sortValue function if provided, sortKey if specified, or fall back to key
        if (column && column.sortValue) {
          aVal = column.sortValue(a);
          bVal = column.sortValue(b);
        } else if (column && column.sortKey) {
          aVal = a[column.sortKey];
          bVal = b[column.sortKey];
        } else {
          aVal = a[sortConfig.key];
          bVal = b[sortConfig.key];
        }

        // Handle null/undefined
        if (aVal === null || aVal === undefined) return 1;
        if (bVal === null || bVal === undefined) return -1;

        // Try to parse as numbers for proper numeric sorting
        const aNum = parseFloat(aVal);
        const bNum = parseFloat(bVal);
        
        if (!isNaN(aNum) && !isNaN(bNum)) {
          return sortConfig.direction === 'asc' ? aNum - bNum : bNum - aNum;
        }

        // String comparison
        const aStr = String(aVal).toLowerCase();
        const bStr = String(bVal).toLowerCase();
        
        if (sortConfig.direction === 'asc') {
          return aStr.localeCompare(bStr);
        } else {
          return bStr.localeCompare(aStr);
        }
      });
    }

    return filtered;
  }, [data, searchTerm, sortConfig, columnFilters, columns, searchableFields, enableSearch, enableColumnFilters]);

  // Export to CSV
  const exportToCSV = () => {
    // Filter out action columns
    const exportColumns = columns.filter(col => col.key !== 'actions' && col.label !== 'Actions');
    const headers = exportColumns.map(col => col.label).join(',');
    const rows = processedData.map(row => 
      exportColumns.map(col => {
        const value = getPlainTextValue(col, row);
        // Escape commas and quotes
        const escaped = String(value || '').replace(/"/g, '""');
        return `"${escaped}"`;
      }).join(',')
    );
    
    const csv = [headers, ...rows].join('\n');
    downloadFile(csv, `${exportFileName}.csv`, 'text/csv');
  };

  // Export to Excel
  const exportToExcel = () => {
    // Filter out action columns
    const exportColumns = columns.filter(col => col.key !== 'actions' && col.label !== 'Actions');
    const headers = exportColumns.map(col => col.label);
    const rows = processedData.map(row =>
      exportColumns.map(col => getPlainTextValue(col, row))
    );

    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
    XLSX.writeFile(wb, `${exportFileName}.xlsx`);
  };

  // Helper to download file
  const downloadFile = (content, fileName, mimeType) => {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      {(enableSearch || enableExport || enableColumnFilters) && (
        <div className="flex flex-col gap-3">
          <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
            {/* Search */}
            {enableSearch && (
              <div className="relative flex-1 max-w-md">
                <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-primary-400 dark:text-primary-500" />
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-10 py-2 text-sm border border-primary-200 dark:border-primary-700 rounded-lg bg-white dark:bg-primary-800 text-primary-900 dark:text-primary-50 placeholder-primary-400 dark:placeholder-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-primary-400 dark:text-primary-500 hover:text-primary-600 dark:hover:text-primary-300"
                  >
                    <X size={18} />
                  </button>
                )}
              </div>
            )}

            {/* Export Buttons */}
            {enableExport && (
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={exportToCSV}
                  leftIcon={<FileText size={16} />}
                  disabled={processedData.length === 0}
                >
                  CSV
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={exportToExcel}
                  leftIcon={<FileSpreadsheet size={16} />}
                  disabled={processedData.length === 0}
                >
                  Excel
                </Button>
              </div>
            )}
          </div>

          {/* Column Filters */}
          {enableColumnFilters && (
            <div className="flex flex-wrap gap-2 items-center">
              <Filter size={16} className="text-primary-600 dark:text-primary-400" />
              <span className="text-xs font-medium text-primary-600 dark:text-primary-400">Column Filters:</span>
              {columns.filter(col => col.key !== 'actions' && col.filterable !== false).map(column => (
                <input
                  key={column.key}
                  type="text"
                  placeholder={`Filter ${column.label}...`}
                  value={columnFilters[column.key] || ''}
                  onChange={(e) => setColumnFilters({ ...columnFilters, [column.key]: e.target.value })}
                  className="px-3 py-1 text-xs border border-primary-200 dark:border-primary-700 rounded-md bg-white dark:bg-primary-800 text-primary-900 dark:text-primary-50 placeholder-primary-400 dark:placeholder-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
              ))}
              {Object.keys(columnFilters).some(key => columnFilters[key]) && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setColumnFilters({})}
                >
                  Clear Filters
                </Button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Results count */}
      {searchTerm && (
        <div className="text-sm text-primary-600 dark:text-primary-400">
          {processedData.length} result{processedData.length !== 1 ? 's' : ''} found
          {processedData.length !== data.length && ` (filtered from ${data.length} total)`}
        </div>
      )}

      {/* Table */}
      {processedData.length === 0 ? (
        <div className="text-center py-12 text-primary-500 dark:text-primary-400">
          {searchTerm ? 'No results found for your search' : emptyMessage}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-primary-200 dark:border-primary-700">
          <table
            className={cn(
              'w-full border-collapse',
              className
            )}
            {...props}
          >
            <thead className="bg-primary-50 dark:bg-primary-900/50">
              <tr className="border-b border-primary-200 dark:border-primary-700">
                {columns.map((column) => {
                  const isSortable = column.sortable !== false;
                  const isSorted = sortConfig.key === column.key;
                  
                  return (
                    <th
                      key={column.key}
                      onClick={() => isSortable && handleSort(column.key)}
                      className={cn(
                        'px-4 py-3 text-left text-sm font-semibold text-primary-700 dark:text-primary-300',
                        isSortable && 'cursor-pointer select-none hover:bg-primary-100 dark:hover:bg-primary-800/50 transition-colors'
                      )}
                    >
                      <div className="flex items-center gap-2">
                        {column.label}
                        {isSortable && (
                          <span className="text-primary-400 dark:text-primary-500">
                            {isSorted ? (
                              sortConfig.direction === 'asc' ? (
                                <SortAsc size={14} />
                              ) : (
                                <SortDesc size={14} />
                              )
                            ) : (
                              <SortAsc size={14} className="opacity-30" />
                            )}
                          </span>
                        )}
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {processedData.map((row, rowIndex) => {
                if (!row) return null;
                return (
                  <tr
                    key={row._id || row.id || rowIndex}
                    className={cn(
                      'border-b border-primary-100 dark:border-primary-800',
                      striped && rowIndex % 2 === 0 && 'bg-primary-50/50 dark:bg-primary-900/30',
                      hover && 'hover:bg-primary-100 dark:hover:bg-primary-800/50 transition-colors'
                    )}
                  >
                    {columns.map((column) => (
                      <td
                        key={column.key}
                        className="px-4 py-3 text-sm text-primary-900 dark:text-primary-50"
                      >
                        {column.render
                          ? column.render(row, rowIndex)
                          : (row[column.key] ?? '')}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default DataTable;
